import { AxiosError } from 'axios';

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Address } from 'viem';

import { get, patch, post } from '@helpers/common/axios.helper';
import { parseAxiosError } from '@helpers/common/error.helpers';

import { removeUndefinedValues } from '@utils/common.utils';

import showToast from '@components/common/Toast';

import { CHAIN_ID } from '@constants/common/chain.constants';
import { SuccessMessages } from '@constants/common/messages.constants';
import {
  FETCH_SIZE_INFINITY,
  INITIAL_PAGE,
} from '@constants/common/pagination.constants';
import { ToastType } from '@constants/common/toast.constants';
import {
  API_SIMILAR_VAULTS,
  API_STATISTICS_APY_PRICE_CHART,
  API_STATISTICS_INVEST_SNAPSHOTS,
  API_STATISTICS_REINVEST_SNAPSHOTS,
  API_STATISTICS_VAULT_ACCOUNT,
  API_STATISTICS_VAULT_FARM_V3_RANGE_CHART,
  API_STATISTICS_WEEKLY_REVENUE,
  API_VAULTS,
  API_VAULTS_ACTION_HISTORY,
  API_VAULTS_ARCHIVE,
  API_VAULTS_AUTOHARVEST_STATUS,
  API_VAULTS_BY_CREATOR,
  API_VAULTS_CLAIMABLE,
  API_VAULTS_CONFIG_LM,
  API_VAULTS_CREATE,
  API_VAULTS_DELETE,
  API_VAULTS_ESTIMATE_APY,
  API_VAULTS_MIGRATION,
  API_VAULTS_PUBLISHED,
  API_VAULTS_SAVE,
  API_VAULTS_SAVED,
  API_VAULTS_UNARCHIVE,
  API_VAULTS_UNSAVE,
  API_VAULTS_UPDATE,
  API_VAULTS_UPDATE_FARMS,
  API_VAULTS_UPDATE_INFO,
  API_VAULT_UPDATE_DATA,
} from '@constants/vaults/api.constants';
import {
  FarmType,
  FarmsFilterTab,
  SortBy,
  SortDestination,
} from '@constants/vaults/common.constants';

import {
  ActionHistory,
  ApyPriceChartDto,
  AutoHarvestStatusDto,
  EstimatedApyDto,
  InvestHistoryDto,
  MigrationVaultDto,
  ReinvestHistoryDto,
  VaultDto,
  VaultFarmDto,
  VaultFarmV3RangeChartDto,
  VaultStatAccountDto,
  WeeklyRevenueDto,
} from '@dto/vaults/vaults.dto';

import { ConnectorRangeWidth } from '@app-types/vaults/vault.types';

// ----------------------------------------------getVaultsPublished-----------------------------------------------------

export type VaultsPublishedParams = {
  search: string | undefined;
  isShowSmallTvls: boolean;
  sortBy: SortBy;
  sortDirection: SortDestination;
  account: Address | undefined;
  staging?: boolean;
  profitTokens?: string[];
  badges?: string[];
  isArchived?: boolean;
  singleFarm?: boolean;
  farmTypes?: FarmsFilterTab[];
  lmType?: string;
  riskLevelFrom?: number;
  riskLevelTo?: number;
  profitPercentFrom?: number;
  profitPercentTo?: number;
  dexFactoryIds?: string[];
  chainIds?: CHAIN_ID[];
  connectorRangeWidthTypes?: ConnectorRangeWidth[];
  limit?: number;
  isUniqByFarms?: boolean;
};

export type UseVaultsPublishedParams = {
  skip?: number;
} & VaultsPublishedParams;

export const getVaultsPublished = (
  params: UseVaultsPublishedParams,
): Promise<VaultDto[]> =>
  get<VaultDto[]>(API_VAULTS_PUBLISHED, {
    ...params,
    limit: params.limit || FETCH_SIZE_INFINITY,
    staging: import.meta.env.VITE_STAGE,
  });

export const useVaultsPublished = (
  params: VaultsPublishedParams,
  isEnabled = true,
) =>
  useInfiniteQuery({
    queryKey: [API_VAULTS_PUBLISHED, params],

    queryFn: ({ pageParam }) =>
      getVaultsPublished({
        skip: pageParam * (params.limit || FETCH_SIZE_INFINITY),
        ...params,
      }),
    initialPageParam: INITIAL_PAGE,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length > 0 ? allPages.length : undefined,
    enabled: !!params && isEnabled,
    refetchOnMount: false,
  });

// ----------------------------------------------getVaultsByCreator-----------------------------------------------------

export type VaultsByCreatorParams = {
  search?: string | undefined;
  sortBy: SortBy;
  sortDirection: SortDestination;
  profitTokens?: string[];
  badges?: string[];
  isArchived?: boolean;
  singleFarm?: boolean;
  farmTypes?: FarmsFilterTab[];
  riskLevelFrom?: number;
  riskLevelTo?: number;
  profitPercentFrom?: number;
  profitPercentTo?: number;
  dexFactoryIds?: string[];
  account?: Address | undefined;
  chainIds?: CHAIN_ID[];
  staging?: boolean;
  lmType?: string;
  connectorRangeWidthTypes?: ConnectorRangeWidth[];
};

type UseVaultsByCreatorParams = { skip?: number } & VaultsByCreatorParams;

export const getVaultsByCreator = (
  params: VaultsByCreatorParams,
): Promise<VaultDto[]> =>
  get<VaultDto[]>(API_VAULTS_BY_CREATOR, {
    ...params,
    limit: FETCH_SIZE_INFINITY,
    staging: import.meta.env.VITE_STAGE,
  });

export const useVaultsByCreator = (params: UseVaultsByCreatorParams) =>
  useInfiniteQuery({
    queryKey: [API_VAULTS_BY_CREATOR, params],
    queryFn: ({ pageParam }) =>
      getVaultsByCreator({
        skip: pageParam * FETCH_SIZE_INFINITY,
        ...params,
      }),
    initialPageParam: INITIAL_PAGE,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length > 0 ? allPages.length : undefined,
    enabled: !!params.account,
    refetchOnMount: false,
  });

// ----------------------------------------------getVaultsSaved-----------------------------------------------------

export type VaultsSavedParams = {
  account: Address | undefined;
  search?: string | undefined;
  lmType?: string;
  sortBy: SortBy;
  sortDirection: SortDestination;
  profitTokens?: string[];
  badges?: string[];
  isArchived?: boolean;
  singleFarm?: boolean;
  farmTypes?: FarmsFilterTab[];
  riskLevelFrom?: number;
  riskLevelTo?: number;
  profitPercentFrom?: number;
  profitPercentTo?: number;
  dexFactoryIds?: string[];
  chainIds?: CHAIN_ID[];
  connectorRangeWidthTypes?: ConnectorRangeWidth[];
};

type UseVaultsSavedParams = { skip?: number } & VaultsSavedParams;

export const getVaultsSaved = (
  params: VaultsSavedParams,
): Promise<VaultDto[]> =>
  get<VaultDto[]>(API_VAULTS_SAVED, { ...params, limit: FETCH_SIZE_INFINITY });

export const useVaultsSaved = (params: UseVaultsSavedParams) =>
  useInfiniteQuery({
    queryKey: [API_VAULTS_SAVED, params],
    queryFn: ({ pageParam }) =>
      getVaultsSaved({
        skip: pageParam * FETCH_SIZE_INFINITY,
        ...params,
      }),
    initialPageParam: INITIAL_PAGE,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length > 0 ? allPages.length : undefined,
    enabled: !!params.account,
    refetchOnMount: false,
  });

// ----------------------------------------------updateData-------------------------------------------------------------

const updateData = (params: {
  vaultId: number;
  account: Address;
}): Promise<VaultDto> => patch<VaultDto>(API_VAULT_UPDATE_DATA, params);

export const useUpdateVaultData = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateData,
    onError: (error) => {
      showToast(
        { title: parseAxiosError(error as AxiosError) },
        ToastType.Error,
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: [API_VAULTS_PUBLISHED] }),
        queryClient.refetchQueries({ queryKey: [API_VAULTS_BY_CREATOR] }),
        queryClient.refetchQueries({ queryKey: [API_VAULTS_SAVED] }),
        queryClient.refetchQueries({ queryKey: [API_VAULTS_CLAIMABLE] }),
        queryClient.refetchQueries({
          queryKey: [API_VAULTS_AUTOHARVEST_STATUS],
        }),
        queryClient.refetchQueries({
          queryKey: [API_STATISTICS_INVEST_SNAPSHOTS],
        }),
        queryClient.refetchQueries({
          queryKey: [API_STATISTICS_VAULT_ACCOUNT],
        }),
        queryClient.refetchQueries({
          queryKey: [API_STATISTICS_REINVEST_SNAPSHOTS],
        }),
        queryClient.refetchQueries({ queryKey: [API_VAULTS_ACTION_HISTORY] }),
      ]);
    },
  });
};

// ----------------------------------------------updateData-------------------------------------------------------------

const updateVaults = (params: {
  vaultIds: number[];
  account: Address;
}): Promise<VaultDto[]> => patch<VaultDto[]>(API_VAULTS_UPDATE, params);

export const useUpdateVaults = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateVaults,
    onError: (error) => {
      showToast(
        { title: parseAxiosError(error as AxiosError) },
        ToastType.Error,
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: [API_VAULTS_CLAIMABLE] }),
        queryClient.refetchQueries({ queryKey: [API_VAULTS_PUBLISHED] }),
        queryClient.refetchQueries({ queryKey: [API_VAULTS_BY_CREATOR] }),
        queryClient.refetchQueries({ queryKey: [API_VAULTS_SAVED] }),
      ]);
    },
  });
};

// ----------------------------------------------archiveVault-----------------------------------------------------------

const archiveVault = (params: {
  data: { vaultId: number; timestamp: number };
  signature?: string;
}): Promise<VaultDto> => patch<VaultDto>(API_VAULTS_ARCHIVE, params);

export const useArchiveVault = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: archiveVault,
    onError: (error) => {
      showToast(
        { title: parseAxiosError(error as AxiosError) },
        ToastType.Error,
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: [API_VAULTS_PUBLISHED] }),
        queryClient.refetchQueries({ queryKey: [API_VAULTS_BY_CREATOR] }),
      ]);
      showToast({ title: SuccessMessages.Archive }, ToastType.Success);
    },
  });
};

// ----------------------------------------------unarchiveVault---------------------------------------------------------

const unarchiveVault = (params: {
  data: { vaultId: number; timestamp: number };
  signature?: string;
}): Promise<VaultDto> => patch<VaultDto>(API_VAULTS_UNARCHIVE, params);

export const useUnarchiveVault = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unarchiveVault,
    onError: (error) => {
      showToast(
        { title: parseAxiosError(error as AxiosError) },
        ToastType.Error,
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: [API_VAULTS_PUBLISHED] }),
        queryClient.refetchQueries({ queryKey: [API_VAULTS_BY_CREATOR] }),
      ]);
      showToast({ title: SuccessMessages.UnArchive }, ToastType.Success);
    },
  });
};

// ----------------------------------------------saveVault-----------------------------------------------------------

const saveVault = (params: {
  data: { vaultId: number; timestamp: number; account: string };
  signature?: string;
}): Promise<VaultDto> => post<VaultDto>(API_VAULTS_SAVE, params);

export const useSaveVault = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: saveVault,
    onError: (error) => {
      showToast(
        { title: parseAxiosError(error as AxiosError) },
        ToastType.Error,
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: [API_VAULTS_PUBLISHED] }),
        queryClient.refetchQueries({ queryKey: [API_VAULTS_SAVED] }),
        queryClient.refetchQueries({ queryKey: [API_VAULTS_BY_CREATOR] }),
        queryClient.refetchQueries({ queryKey: [API_SIMILAR_VAULTS] }),
      ]);
      showToast({ title: SuccessMessages.Saved }, ToastType.Success);
    },
  });
};

// ----------------------------------------------unsaveVault-----------------------------------------------------------

const unSaveVault = (params: {
  data: { vaultId: number; timestamp: number; account: string };
  signature?: string;
}): Promise<VaultDto> => post<VaultDto>(API_VAULTS_UNSAVE, params);

export const useUnSaveVault = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unSaveVault,
    onError: (error) => {
      showToast(
        { title: parseAxiosError(error as AxiosError) },
        ToastType.Error,
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: [API_VAULTS_PUBLISHED] }),
        queryClient.refetchQueries({ queryKey: [API_VAULTS_BY_CREATOR] }),
        queryClient.refetchQueries({ queryKey: [API_VAULTS_SAVED] }),
        queryClient.refetchQueries({ queryKey: [API_SIMILAR_VAULTS] }),
      ]);
      showToast({ title: SuccessMessages.UnSaved }, ToastType.Success);
    },
  });
};

// ----------------------------------------------deleteVault------------------------------------------------------------

const deleteVault = (params: {
  data: { vaultId: number; timestamp: number };
  signature?: string;
}): Promise<VaultDto> => patch<VaultDto>(API_VAULTS_DELETE, params);

export const useDeleteVault = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteVault,
    onError: (error) => {
      showToast(
        { title: parseAxiosError(error as AxiosError) },
        ToastType.Error,
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: [API_VAULTS_PUBLISHED] }),
        queryClient.refetchQueries({ queryKey: [API_VAULTS_BY_CREATOR] }),
        queryClient.refetchQueries({ queryKey: [API_VAULTS_SAVED] }),
      ]);
      showToast({ title: SuccessMessages.Deleted }, ToastType.Success);
    },
  });
};

// ----------------------------------------------getVaultById-----------------------------------------------------------

const getVaultById = ({
  vaultId,
  account,
}: {
  vaultId: number;
  account?: Address;
}): Promise<VaultDto> =>
  get<VaultDto>(`${API_VAULTS}/${vaultId}`, {
    account,
    staging: import.meta.env.VITE_STAGE,
  });

export const useGetVaultById = (params: {
  vaultId: number;
  account?: Address;
  isPending: boolean;
}) =>
  useQuery<VaultDto>({
    queryKey: [`${API_VAULTS}/${params.vaultId}`, params],
    queryFn: () => getVaultById(params),
    retry: false,
    enabled: !params.isPending || !params.vaultId,
  });

// ----------------------------------------------updateVaultFarms-------------------------------------------------------

const updateVaultFarms = (params: { vaultId: number }): Promise<VaultDto> =>
  post<VaultDto>(API_VAULTS_UPDATE_FARMS, params);

export const useUpdateVaultFarms = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateVaultFarms,
    onError: (error) => {
      showToast({ title: error.message }, ToastType.Error);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: [API_VAULTS_PUBLISHED] }),
        queryClient.refetchQueries({ queryKey: [API_VAULTS_BY_CREATOR] }),
      ]);
    },
  });
};

// ----------------------------------------------getVaultActionHistory--------------------------------------------------

type ActionHistoryParams = {
  vaultId: number;
  account: Address | undefined;
  isTotalMetrics: boolean;
};

const getVaultActionHistory = (
  params: ActionHistoryParams,
): Promise<ActionHistory[]> =>
  get<ActionHistory[]>(API_VAULTS_ACTION_HISTORY, params);

export const useVaultActionHistory = (params: ActionHistoryParams) =>
  useQuery<ActionHistory[]>({
    queryKey: [API_VAULTS_ACTION_HISTORY, params],
    queryFn: () => getVaultActionHistory(params),
    retry: false,
  });

// ----------------------------------------------estimateApy------------------------------------------------------------

export type EstimateApyFarm = {
  farmId: number;
  percent: number;
  type: FarmType;
  sqrtPriceAX96: string | undefined;
  sqrtPriceBX96: string | undefined;
};

export type EstimateApyParams = {
  farms: EstimateApyFarm[];
  takeProfitPercent: number;
  vaultFactoryId: number;
  chainId: number;
  dummyInvestUsd: number;
};

const estimateApy = (params: EstimateApyParams): Promise<EstimatedApyDto> =>
  post<EstimatedApyDto>(API_VAULTS_ESTIMATE_APY, params);

export const useEstimateApy = () =>
  useMutation({
    mutationFn: estimateApy,
    onError: (error) => {
      showToast({ title: error.message }, ToastType.Error);
    },
  });

// ----------------------------------------------createVault------------------------------------------------------------

const createVault = (params: {
  txHash: string;
  vaultFactoryId: number;
  chainId: number;
}): Promise<VaultDto> => post<VaultDto>(API_VAULTS_CREATE, params);

export const useCreateVault = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createVault,
    onError: (error) => {
      showToast({ title: error.message }, ToastType.Error);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: [API_VAULTS_PUBLISHED] }),
        queryClient.refetchQueries({ queryKey: [API_VAULTS_BY_CREATOR] }),
      ]);
    },
  });
};

// ----------------------------------------------getVaultClaimable------------------------------------------------------

export type VaultsClaimableParams = {
  account: Address | undefined;
  chainIds: CHAIN_ID[];
};

const getVaultClaimable = (
  params: VaultsClaimableParams,
): Promise<VaultDto[]> =>
  get<VaultDto[]>(API_VAULTS_CLAIMABLE, {
    ...params,
    staging: import.meta.env.VITE_STAGE,
  });

export const useVaultClaimable = (params: VaultsClaimableParams) =>
  useQuery<VaultDto[]>({
    queryKey: [API_VAULTS_CLAIMABLE, params],
    queryFn: () => getVaultClaimable(params),
    retry: false,
    refetchOnMount: false,
  });

// ----------------------------------------------updateVaultInfo--------------------------------------------------------

type UpdateVaultInfoParamsType = {
  data: {
    vaultId: number;
    title: string;
    description: string;
    publisherName: string;
    timestamp: number;
  };
  signature?: string;
};

const updateVaultInfo = (
  params: UpdateVaultInfoParamsType,
): Promise<VaultDto> => patch<VaultDto>(API_VAULTS_UPDATE_INFO, params);

export const useUpdateVaultInfo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateVaultInfo,
    onError: (error) => {
      showToast({ title: error.message }, ToastType.Error);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: [API_VAULTS_PUBLISHED] }),
        queryClient.refetchQueries({ queryKey: [API_VAULTS_BY_CREATOR] }),
      ]);
    },
  });
};

// ----------------------------------------------getAutoharvestStatus---------------------------------------------------

const getAutoharvestStatus = (params: {
  vaultId: number;
}): Promise<AutoHarvestStatusDto> =>
  get<AutoHarvestStatusDto>(API_VAULTS_AUTOHARVEST_STATUS, params);

export const useAutoharvestStatus = (params: { vaultId: number }) =>
  useQuery<AutoHarvestStatusDto>({
    queryKey: [API_VAULTS_AUTOHARVEST_STATUS, params],
    queryFn: () => getAutoharvestStatus(params),
    retry: false,
  });

// ----------------------------------------------getVaultsMigration-----------------------------------------------------

const getVaultsMigration = (params: {
  vaultId: number;
  account: string;
  chainIds: number[];
}): Promise<MigrationVaultDto> =>
  get<MigrationVaultDto>(API_VAULTS_MIGRATION, params);

export const useVaultsMigration = (params: {
  vaultId: number;
  account: string;
  chainIds: number[];
}) =>
  useQuery<MigrationVaultDto>({
    queryKey: [API_VAULTS_MIGRATION, params],
    queryFn: () => getVaultsMigration(params),
    retry: false,
  });

// ----------------------------------------------getSimilarVaults-------------------------------------------------------

type SimilarVaultsParams = {
  chainId: number | undefined;
  farmIds: number[];
  account?: Address | undefined;
};

const getSimilarVaults = (params: SimilarVaultsParams): Promise<VaultDto[]> =>
  get<VaultDto[]>(API_SIMILAR_VAULTS, params);

export const useGetSimilarVaults = (params: SimilarVaultsParams) =>
  useQuery<VaultDto[]>({
    queryKey: [API_SIMILAR_VAULTS, params],
    queryFn: () => getSimilarVaults(params),
    retry: false,
    enabled: !!params.farmIds.length && !!params.chainId,
  });

// ----------------------------------------------getInvestChart-------------------------------------------------------

type InvestChartParams = {
  vaultId: number;
  account?: Address | undefined;
  isTotalMetrics: boolean;
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
};

const getInvestData = (params: InvestChartParams): Promise<InvestHistoryDto> =>
  get<InvestHistoryDto>(API_STATISTICS_INVEST_SNAPSHOTS, params);

export const useGetInvestData = (
  params: InvestChartParams,
  enabled?: boolean,
) => {
  const filteredParams = removeUndefinedValues(params);

  return useQuery<InvestHistoryDto>({
    queryKey: [API_STATISTICS_INVEST_SNAPSHOTS, filteredParams],
    queryFn: () => getInvestData(filteredParams),
    retry: false,
    enabled,
  });
};

// ----------------------------------------------getReInvestChart-------------------------------------------------------

type ReinvestChartParams = {
  vaultId: number;
  account?: Address | undefined;
  isTotalMetrics: boolean;
  enabled?: boolean;
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
};

const getReinvestData = (
  params: ReinvestChartParams,
): Promise<ReinvestHistoryDto> =>
  get<ReinvestHistoryDto>(API_STATISTICS_REINVEST_SNAPSHOTS, params);

export const useGetReinvestData = (
  params: ReinvestChartParams,
  enabled?: boolean,
) => {
  const filteredParams = removeUndefinedValues(params);

  return useQuery<ReinvestHistoryDto>({
    queryKey: [API_STATISTICS_REINVEST_SNAPSHOTS, filteredParams],
    queryFn: () => getReinvestData(filteredParams),
    retry: false,
    enabled,
  });
};

// ----------------------------------------------getAccountVaultStats-------------------------------------------------------

type AccountVaultStatsParams = {
  vaultId: number;
  account: Address | undefined;
};

const getAccountVaultStats = (
  params: AccountVaultStatsParams,
): Promise<VaultStatAccountDto> =>
  get<VaultStatAccountDto>(
    `${API_STATISTICS_VAULT_ACCOUNT}/${params.account}`,
    params,
  );

export const useGetAccountVaultStats = (
  params: AccountVaultStatsParams,
  enabled?: boolean,
) =>
  useQuery<VaultStatAccountDto>({
    queryKey: [`${API_STATISTICS_VAULT_ACCOUNT}/${params.account}`, params],
    queryFn: () => getAccountVaultStats(params),
    retry: false,
    enabled: !!params.account && enabled,
  });

// ----------------------------------------------updateConfigLm---------------------------------------------------------

type ConfigLmParams = {
  data: {
    vaultId: number;
    farmId: number;
    account: string;
    isAilmEnabled: boolean;
    isAlmEnabled: boolean;
    timestamp: number;
  };
  signature?: string;
};

const updateConfigLm = (params: ConfigLmParams): Promise<VaultFarmDto> =>
  post<VaultFarmDto>(API_VAULTS_CONFIG_LM, params);

export const useUpdateConfigLm = () =>
  useMutation({
    mutationFn: updateConfigLm,
    onError: (error) => {
      showToast(
        { title: parseAxiosError(error as AxiosError) },
        ToastType.Error,
      );
    },
    onSuccess: () => {
      showToast({ title: SuccessMessages.Updated }, ToastType.Success);
    },
  });

// ----------------------------------------------getWeeklyRevenue-------------------------------------------------------

const getWeeklyRevenue = (): Promise<WeeklyRevenueDto> =>
  get<WeeklyRevenueDto>(API_STATISTICS_WEEKLY_REVENUE);

export const useGetWeeklyRevenue = () =>
  useQuery<WeeklyRevenueDto>({
    queryKey: [API_STATISTICS_WEEKLY_REVENUE],
    queryFn: () => getWeeklyRevenue(),
    retry: false,
  });

// ----------------------------------------------getVaultFarmV3Range--------------------------------------------------

type VaultFarmV3RangeParams = {
  vaultId: number;
  farmId: number;
  isTotalMetrics: boolean;
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
};

const getVaultFarmV3Range = (
  params: VaultFarmV3RangeParams,
): Promise<VaultFarmV3RangeChartDto[]> =>
  get<VaultFarmV3RangeChartDto[]>(
    API_STATISTICS_VAULT_FARM_V3_RANGE_CHART,
    params,
  );

export const useVaultFarmV3Range = (params: VaultFarmV3RangeParams) => {
  const filteredParams = removeUndefinedValues(params);

  return useQuery<VaultFarmV3RangeChartDto[]>({
    queryKey: [API_STATISTICS_VAULT_FARM_V3_RANGE_CHART, filteredParams],
    queryFn: () => getVaultFarmV3Range(filteredParams),
    retry: false,
    enabled: !!filteredParams.vaultId && !!filteredParams.farmId,
  });
};

// ----------------------------------------------getApyPriceChart--------------------------------------------------

type ApyPriceChartParams = {
  vaultId: number;
  isTotalMetrics: boolean;
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
};

const getApyPriceChart = (
  params: ApyPriceChartParams,
): Promise<ApyPriceChartDto[]> =>
  get<ApyPriceChartDto[]>(API_STATISTICS_APY_PRICE_CHART, params);

export const useGetApyPriceChart = (params: ApyPriceChartParams) => {
  const filteredParams = removeUndefinedValues(params);
  return useQuery<ApyPriceChartDto[]>({
    queryKey: [API_STATISTICS_APY_PRICE_CHART, filteredParams],
    queryFn: () => getApyPriceChart(filteredParams),
    retry: false,
  });
};
